/**
 * SettingsEditor Container Component
 *
 * Main container for LLM settings management.
 * Uses a three-level selection system: Provider -> Pool -> Model.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { ProviderSelector, type ProviderOption } from './ProviderSelector';
import { PoolSelector, type PoolOption } from './PoolSelector';
import { ModelSelector, type ModelOption } from './ModelSelector';
import { QuotaEditor, type QuotaLimits } from './QuotaEditor';
import { PriorityDragger, type PriorityItem } from './PriorityDragger';
import { QuotaProgress } from '../shared/QuotaProgress';
import { llmAdminApi } from '../../../services/llmAdminApi';
import { StatusBadge } from '../shared/StatusBadge';
import { ApiKeyManager } from '../ApiKeyManager';
import { CreateEntityDialog, type CreateField } from './CreateEntityDialog';

export interface SettingsEditorProps {
  onSettingsChange?: (settings: Record<string, unknown>) => void;
  disabled?: boolean;
  className?: string;
}

interface QuotaUsageItem {
  used: number;
  total: number | false;
}

interface QuotaUsage {
  [provider: string]: {
    [poolIndex: string]: {
      concurrency?: QuotaUsageItem;
      token_per_minute?: QuotaUsageItem;
      token_per_hour?: QuotaUsageItem;
      token_per_day?: QuotaUsageItem;
      token_per_month?: QuotaUsageItem;
      token_total?: QuotaUsageItem;
      request_per_minute?: QuotaUsageItem;
      request_per_hour?: QuotaUsageItem;
      request_per_day?: QuotaUsageItem;
      request_per_month?: QuotaUsageItem;
      request_total?: QuotaUsageItem;
    };
  };
}

interface ModelConfig {
  model_name: string;
  model_use: string;
  model_type?: string;
  priority?: number;
  base_url?: string;
  health?: {
    available: boolean;
    latency_ms: number;
    error_count: number;
  };
  quota?: QuotaLimits;
}

interface PoolKeyConfig {
  key?: string;
  status?: string;
  used_quota?: number;
  error_count?: number;
}

interface PoolConfig {
  api_key?: string;
  base_url?: string;
  quota?: QuotaLimits;
  key_selection_mode?: 'polling' | 'random';
  weight?: number;
  model?: ModelConfig[];
  keys?: PoolKeyConfig[];
}

interface ProviderConfig {
  base_url?: string;
  pool?: PoolConfig[];
}

interface RawSettings {
  provider?: Record<string, ProviderConfig>;
}

export const SettingsEditor: React.FC<SettingsEditorProps> = ({
  onSettingsChange,
  disabled = false,
  className = '',
}) => {
  const [rawSettings, setRawSettings] = useState<RawSettings>({});
  const [priorities, setPriorities] = useState<PriorityItem[]>([]);
  const [quotaUsage, setQuotaUsage] = useState<QuotaUsage>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'providers' | 'priorities'>('providers');

  // Selection states
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedPool, setSelectedPool] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  // UI states
  const [showApiKey, setShowApiKey] = useState(false);
  const [showMultiKey, setShowMultiKey] = useState(false);
  
  // Collapse states for config cards
  const [isProviderCollapsed, setIsProviderCollapsed] = useState(false);
  const [isPoolCollapsed, setIsPoolCollapsed] = useState(false);
  const [isModelCollapsed, setIsModelCollapsed] = useState(false);

  // Health check states
  const [checkingPoolHealth, setCheckingPoolHealth] = useState(false);
  const [checkingModelHealth, setCheckingModelHealth] = useState(false);
  const [poolHealthResult, setPoolHealthResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [modelHealthResult, setModelHealthResult] = useState<{ valid: boolean; message: string } | null>(null);

  // Create dialog states
  const [createDialog, setCreateDialog] = useState<'provider' | 'pool' | 'model' | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settingsResponse = await llmAdminApi.getSettings();
      const settings = settingsResponse.settings as RawSettings;
      setRawSettings(settings);

      // Build priority list
      const priorityList: PriorityItem[] = [];
      if (settings?.provider) {
        Object.entries(settings.provider).forEach(([name, config]) => {
          config.pool?.forEach((_, poolIndex) => {
            priorityList.push({
              id: `${name}-${poolIndex}`,
              providerName: name,
              poolIndex,
              label: `${name} / 池 #${poolIndex}`,
            });
          });
        });
      }
      setPriorities(priorityList);

      // Load quota usage
      try {
        const quotaResponse = await fetch('/api/admin/llm/stats/quotas', {
          credentials: 'include'
        });
        if (quotaResponse.ok) {
          const quotaData = await quotaResponse.json();
          setQuotaUsage(quotaData.usage || {});
        }
      } catch {
        // Quota data is optional
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载设置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset selections when provider changes
  useEffect(() => {
    setSelectedPool(null);
    setSelectedModel(null);
  }, [selectedProvider]);

  // Reset model selection when pool changes
  useEffect(() => {
    setSelectedModel(null);
  }, [selectedPool]);

  // Memoized provider options
  const providerOptions: ProviderOption[] = useMemo(() => {
    if (!rawSettings.provider) return [];
    return Object.entries(rawSettings.provider).map(([name, config]) => {
      const pools = config.pool || [];
      const models = pools.flatMap(p => p.model || []);
      
      // Calculate health status
      let healthyCount = 0;
      let unhealthyCount = 0;
      models.forEach(m => {
        if (m.health?.available) healthyCount++;
        else if (m.health && !m.health.available) unhealthyCount++;
      });
      
      let healthStatus: 'healthy' | 'unhealthy' | 'unknown' = 'unknown';
      if (unhealthyCount > 0) healthStatus = 'unhealthy';
      else if (healthyCount > 0) healthStatus = 'healthy';

      return {
        name,
        baseUrl: config.base_url || '',
        poolCount: pools.length,
        modelCount: models.filter(m => m.model_name && m.model_use).length,
        healthStatus,
      };
    });
  }, [rawSettings]);

  // Memoized pool options for selected provider
  const poolOptions: PoolOption[] = useMemo(() => {
    if (!selectedProvider || !rawSettings.provider?.[selectedProvider]) return [];
    const pools = rawSettings.provider[selectedProvider].pool || [];
    return pools.map((pool, index) => ({
      poolIndex: index,
      apiKeyPreview: pool.api_key || '',
      hasApiKey: !!pool.api_key,
      modelCount: (pool.model || []).filter(m => m.model_name && m.model_use).length,
      weight: pool.weight ?? 10,
      keySelectionMode: pool.key_selection_mode || 'polling',
    }));
  }, [rawSettings, selectedProvider]);

  const modelOptions: ModelOption[] = useMemo(() => {
    if (!selectedProvider || selectedPool === null) return [];
    const pool = rawSettings.provider?.[selectedProvider]?.pool?.[selectedPool];
    if (!pool) return [];
    return (pool.model || []).map(m => ({
      model_use: m.model_use,
      model_name: m.model_name,
      model_type: m.model_type,
      priority: m.priority,
      healthStatus: m.health?.available ? 'healthy' : 
                    (m.health && !m.health.available) ? 'unhealthy' : 'unknown',
      latency: m.health?.latency_ms,
    }));
  }, [rawSettings, selectedProvider, selectedPool]);

  // Get current selected entities
  const currentProvider = selectedProvider ? rawSettings.provider?.[selectedProvider] : null;
  const currentPool = currentProvider && selectedPool !== null 
    ? currentProvider.pool?.[selectedPool] 
    : null;
  const currentModel = currentPool?.model?.find(m => m.model_use === selectedModel);

  // Handlers
  const handleAddProvider = useCallback(() => {
    setCreateError(null);
    setCreateDialog('provider');
  }, []);

  const handleDeleteProvider = useCallback(async (name: string) => {
    try {
      await llmAdminApi.deleteProvider(undefined, name);
      if (selectedProvider === name) {
        setSelectedProvider(null);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除提供商失败');
    }
  }, [selectedProvider, loadData]);

  const handleUpdateProvider = useCallback((updates: Partial<ProviderConfig>) => {
    if (!selectedProvider) return;
    setRawSettings(prev => ({
      ...prev,
      provider: {
        ...prev.provider,
        [selectedProvider]: { ...prev.provider?.[selectedProvider], ...updates }
      }
    }));
  }, [selectedProvider]);

  const handleAddPool = useCallback(() => {
    setCreateError(null);
    setCreateDialog('pool');
  }, []);

  const handleDeletePool = useCallback(async (poolIndex: number) => {
    if (!selectedProvider) return;
    try {
      await llmAdminApi.deletePool(undefined, selectedProvider, poolIndex);
      if (selectedPool === poolIndex) {
        setSelectedPool(null);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除池失败');
    }
  }, [selectedProvider, selectedPool, loadData]);

  const handleUpdatePool = useCallback((poolIndex: number, updates: Partial<PoolConfig>) => {
    if (!selectedProvider) return;
    setRawSettings(prev => {
      const provider = prev.provider?.[selectedProvider];
      const newPools = [...(provider?.pool || [])];
      newPools[poolIndex] = { ...newPools[poolIndex], ...updates };
      return {
        ...prev,
        provider: {
          ...prev.provider,
          [selectedProvider]: { ...provider, pool: newPools }
        }
      };
    });
  }, [selectedProvider]);

  const handleAddModel = useCallback(() => {
    setCreateError(null);
    setCreateDialog('model');
  }, []);

  const handleDeleteModel = useCallback(async (modelUse: string) => {
    if (!selectedProvider || selectedPool === null) return;
    try {
      await llmAdminApi.deleteModel(undefined, selectedProvider, selectedPool, modelUse);
      if (selectedModel === modelUse) {
        setSelectedModel(null);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除模型失败');
    }
  }, [selectedProvider, selectedPool, selectedModel, loadData]);

  const handleUpdateModel = useCallback((modelUse: string, updates: Partial<ModelConfig>) => {
    if (!selectedProvider || selectedPool === null) return;
    if (updates.model_use && updates.model_use !== modelUse) {
      setSelectedModel(updates.model_use);
    }
    setRawSettings(prev => {
      const provider = prev.provider?.[selectedProvider];
      const pools = [...(provider?.pool || [])];
      const models = (pools[selectedPool]?.model || []).map(m =>
        m.model_use === modelUse ? { ...m, ...updates } : m
      );
      pools[selectedPool] = { ...pools[selectedPool], model: models };
      return {
        ...prev,
        provider: {
          ...prev.provider,
          [selectedProvider]: { ...provider, pool: pools }
        }
      };
    });
  }, [selectedProvider, selectedPool]);

  const handleCancelCreate = useCallback(() => {
    setCreateDialog(null);
    setCreateError(null);
  }, []);

  const handleCreateProvider = useCallback(async (values: Record<string, string>) => {
    setCreating(true);
    setCreateError(null);
    try {
      await llmAdminApi.createProvider(undefined, {
        name: values.name,
        base_url: values.base_url,
      });
      setSelectedProvider(values.name);
      setCreateDialog(null);
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : '创建提供商失败');
    } finally {
      setCreating(false);
    }
  }, [loadData]);

  const handleCreatePool = useCallback(async (values: Record<string, string>) => {
    if (!selectedProvider) return;
    setCreating(true);
    setCreateError(null);
    try {
      await llmAdminApi.createPool(undefined, selectedProvider, {
        api_key: values.api_key || '',
      });
      const provider = rawSettings.provider?.[selectedProvider];
      setSelectedPool((provider?.pool || []).length);
      setCreateDialog(null);
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : '创建池失败');
    } finally {
      setCreating(false);
    }
  }, [selectedProvider, rawSettings, loadData]);

  const handleCreateModel = useCallback(async (values: Record<string, string>) => {
    if (!selectedProvider || selectedPool === null) return;
    setCreating(true);
    setCreateError(null);
    try {
      await llmAdminApi.createModel(undefined, selectedProvider, selectedPool, {
        model_name: values.model_name,
        model_use: values.model_use,
        model_type: values.model_type || 'chat',
      });
      setSelectedModel(values.model_use);
      setCreateDialog(null);
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : '创建模型失败');
    } finally {
      setCreating(false);
    }
  }, [selectedProvider, selectedPool, loadData]);

  const providerCreateFields: CreateField[] = useMemo(() => [
    {
      name: 'name',
      label: '提供商名称',
      type: 'text',
      required: true,
      placeholder: '例如: openai',
      hint: '唯一标识符，创建后不可修改',
      defaultValue: `provider-${Object.keys(rawSettings.provider || {}).length + 1}`,
    },
    {
      name: 'base_url',
      label: 'Base URL',
      type: 'url',
      required: true,
      placeholder: '例如: https://api.openai.com/v1',
      hint: 'API 请求的基础 URL 地址',
    },
  ], [rawSettings.provider]);

  const poolCreateFields: CreateField[] = useMemo(() => [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      required: false,
      placeholder: '输入 API Key（可稍后配置）',
      hint: 'API 密钥，可稍后在池配置中添加或使用 Multi-Key 管理',
    },
  ], []);

  const modelCreateFields: CreateField[] = useMemo(() => [
    {
      name: 'model_use',
      label: '模型标识 (model_use)',
      type: 'text',
      required: true,
      placeholder: '例如: gpt-4',
      hint: '应用程序使用的模型标识符，创建后不可修改',
    },
    {
      name: 'model_name',
      label: '实际模型名称 (model_name)',
      type: 'text',
      required: true,
      placeholder: '例如: gpt-4-0125-preview',
      hint: 'API 调用时使用的实际模型名称',
    },
    {
      name: 'model_type',
      label: '模型类型',
      type: 'select',
      defaultValue: 'chat',
      options: [
        { value: 'chat', label: '对话 (chat)' },
        { value: 'write', label: '写作 (write)' },
        { value: 'label', label: '标签 (label)' },
        { value: 'summary', label: '摘要 (summary)' },
        { value: 'image', label: '图像生成 (image)' },
        { value: 'graphiti', label: 'Graphiti' },
        { value: 'embedding', label: 'Embedding' },
        { value: 'rerank', label: 'Rerank' },
      ],
    },
  ], []);

  const handlePriorityChange = useCallback((items: PriorityItem[]) => {
    setPriorities(items);
  }, []);

  // Health check handlers
  const handleCheckPoolHealth = useCallback(async () => {
    if (!selectedProvider || selectedPool === null) return;
    setCheckingPoolHealth(true);
    setPoolHealthResult(null);
    try {
      const result = await llmAdminApi.validatePool(undefined, selectedProvider, selectedPool) as {
        results?: Array<{ valid: boolean; model_use: string; model_name: string; response_time_ms?: number; error_message?: string }>;
        total_models?: number;
        valid_models?: number;
      };
      const validModels = result.valid_models || 0;
      const totalModels = result.total_models || 0;
      setPoolHealthResult({
        valid: validModels > 0,
        message: `池健康检查完成: ${validModels}/${totalModels} 个模型可用`,
      });
      // Reload data to get updated health status
      await loadData();
    } catch (err) {
      setPoolHealthResult({
        valid: false,
        message: err instanceof Error ? err.message : '健康检查失败',
      });
    } finally {
      setCheckingPoolHealth(false);
    }
  }, [selectedProvider, selectedPool, loadData]);

  const handleCheckModelHealth = useCallback(async () => {
    if (!selectedProvider || selectedPool === null || !selectedModel) return;
    setCheckingModelHealth(true);
    setModelHealthResult(null);
    try {
      const result = await llmAdminApi.validateModel(undefined, selectedModel);
      setModelHealthResult({
        valid: result.valid,
        message: result.valid
          ? `模型可用 (响应时间: ${result.response_time_ms}ms)`
          : `模型不可用: ${result.error_message || '未知错误'}`,
      });
      // Reload data to get updated health status
      await loadData();
    } catch (err) {
      setModelHealthResult({
        valid: false,
        message: err instanceof Error ? err.message : '健康检查失败',
      });
    } finally {
      setCheckingModelHealth(false);
    }
  }, [selectedProvider, selectedPool, selectedModel, loadData]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await llmAdminApi.updateSettings(undefined, rawSettings as never);
      setSuccess('设置保存成功');
      onSettingsChange?.(rawSettings as Record<string, unknown>);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存设置失败');
    } finally {
      setSaving(false);
    }
  }, [rawSettings, onSettingsChange]);

  if (loading) {
    return (
      <div className={clsx('p-8 text-center text-gray-500', className)}>
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        加载中...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setActiveView('providers')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeView === 'providers'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            提供商管理
          </button>
          <button
            type="button"
            onClick={() => setActiveView('priorities')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeView === 'priorities'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            优先级配置
          </button>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            刷新
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || saving}
            className={clsx(
              'px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium',
              'hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {saving ? '保存中...' : '保存设置'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700">
            关闭
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Providers View */}
      {activeView === 'providers' && (
        <div className="flex gap-6" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
          {/* Left Column - Selectors */}
          <div className="w-80 flex-shrink-0 overflow-y-auto pr-2 space-y-4">
            <ProviderSelector
              providers={providerOptions}
              selectedProvider={selectedProvider}
              onSelect={setSelectedProvider}
              onAdd={handleAddProvider}
              onDelete={handleDeleteProvider}
              disabled={disabled}
            />

            {selectedProvider && (
              <PoolSelector
                pools={poolOptions}
                selectedPool={selectedPool}
                onSelect={setSelectedPool}
                onAdd={handleAddPool}
                onDelete={handleDeletePool}
                disabled={disabled}
              />
            )}

            {selectedProvider && selectedPool !== null && (
              <ModelSelector
                models={modelOptions}
                selectedModel={selectedModel}
                onSelect={setSelectedModel}
                onAdd={handleAddModel}
                onDelete={handleDeleteModel}
                disabled={disabled}
              />
            )}
          </div>

          {/* Right Column - Editor */}
          <div className="flex-1 overflow-y-auto min-w-0">
            {!selectedProvider ? (
              <div className="h-full flex items-center justify-center p-12 border-2 border-dashed border-gray-200 rounded-lg">
                <div className="text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p>请从左侧选择一个提供商开始配置</p>
                </div>
              </div>
            ) : !currentProvider ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
                提供商不存在或已被删除
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* Provider Settings */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-800">提供商配置</h3>
                    <div className="flex items-center gap-2">
                      <StatusBadge variant="info" size="sm">{selectedProvider}</StatusBadge>
                      <button
                        type="button"
                        onClick={() => setIsProviderCollapsed(!isProviderCollapsed)}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                        title={isProviderCollapsed ? '展开' : '折叠'}
                      >
                        <svg
                          className={clsx('w-5 h-5 transition-transform', isProviderCollapsed ? '' : 'rotate-180')}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className={clsx('space-y-4', isProviderCollapsed && 'hidden')}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        提供商名称
                      </label>
                      <input
                        type="text"
                        value={selectedProvider}
                        onChange={(e) => {
                          const newName = e.target.value;
                          if (newName && newName !== selectedProvider) {
                            setRawSettings(prev => {
                              const providers = { ...prev.provider };
                              providers[newName] = providers[selectedProvider];
                              delete providers[selectedProvider];
                              return { ...prev, provider: providers };
                            });
                            setSelectedProvider(newName);
                          }
                        }}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Base URL
                      </label>
                      <input
                        type="url"
                        value={currentProvider.base_url || ''}
                        onChange={(e) => handleUpdateProvider({ base_url: e.target.value })}
                        disabled={disabled}
                        placeholder="例如: https://api.openai.com/v1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                      />
                      <p className="mt-1 text-xs text-gray-500">API 请求的基础 URL 地址</p>
                    </div>
                  </div>
                </div>

                {/* Pool Settings */}
                {selectedPool !== null && currentPool && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-800">池配置</h3>
                      <div className="flex items-center gap-2">
                        <StatusBadge variant="info" size="sm">池 #{selectedPool}</StatusBadge>
                        <button
                          type="button"
                          onClick={handleCheckPoolHealth}
                          disabled={checkingPoolHealth}
                          className={clsx(
                            'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5',
                            checkingPoolHealth
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          )}
                          title="检查池中所有模型的可用性"
                        >
                          {checkingPoolHealth ? (
                            <>
                              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              检查中...
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              健康检查
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsPoolCollapsed(!isPoolCollapsed)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                          title={isPoolCollapsed ? '展开' : '折叠'}
                        >
                          <svg
                            className={clsx('w-5 h-5 transition-transform', isPoolCollapsed ? '' : 'rotate-180')}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {/* Pool Health Check Result */}
                    {poolHealthResult && (
                      <div className={clsx(
                        'mb-4 p-3 rounded-lg text-sm',
                        poolHealthResult.valid
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : 'bg-red-50 border border-red-200 text-red-700'
                      )}>
                        <div className="flex items-center gap-2">
                          {poolHealthResult.valid ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                          {poolHealthResult.message}
                        </div>
                      </div>
                    )}
                    <div className={clsx('space-y-4', isPoolCollapsed && 'hidden')}>
                      {/* API Key */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKey ? 'text' : 'password'}
                            value={currentPool.api_key || ''}
                            onChange={(e) => handleUpdatePool(selectedPool, { api_key: e.target.value })}
                            disabled={disabled}
                            placeholder="输入 API Key"
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showApiKey ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Key Selection Mode & Weight */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Key 选择策略
                          </label>
                          <select
                            value={currentPool.key_selection_mode || 'polling'}
                            onChange={(e) => handleUpdatePool(selectedPool, { 
                              key_selection_mode: e.target.value as 'polling' | 'random' 
                            })}
                            disabled={disabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                          >
                            <option value="polling">轮询 (Polling)</option>
                            <option value="random">随机 (Random)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            路由权重 (0-100)
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={currentPool.weight ?? 10}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val) && val >= 0 && val <= 100) {
                                handleUpdatePool(selectedPool, { weight: val });
                              }
                            }}
                            disabled={disabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                          />
                        </div>
                      </div>

                      {/* Pool Base URL */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          池级 Base URL（可选）
                        </label>
                        <input
                          type="url"
                          value={currentPool.base_url || ''}
                          onChange={(e) => handleUpdatePool(selectedPool, { base_url: e.target.value })}
                          disabled={disabled}
                          placeholder="覆盖提供商级 Base URL"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                        />
                      </div>

                      {/* Pool Quota */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          池级别配额
                        </label>
                        <QuotaEditor
                          value={currentPool.quota || {}}
                          onChange={(quota) => handleUpdatePool(selectedPool, { quota })}
                          disabled={disabled}
                          defaultCollapsed={true}
                        />
                      </div>

                      {/* Multi-Key Manager */}
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Multi-Key 管理</h4>
                          <button
                            type="button"
                            onClick={() => setShowMultiKey(!showMultiKey)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            {showMultiKey ? '收起' : '展开'}
                          </button>
                        </div>
                        {showMultiKey && selectedProvider && (
                          <ApiKeyManager
                            providerName={selectedProvider}
                            poolIndex={selectedPool}
                            disabled={disabled}
                            onRefresh={loadData}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Model Settings */}
                {selectedModel && currentModel && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-800">模型配置</h3>
                      <div className="flex items-center gap-2">
                        <StatusBadge variant="info" size="sm">{selectedModel}</StatusBadge>
                        <button
                          type="button"
                          onClick={handleCheckModelHealth}
                          disabled={checkingModelHealth}
                          className={clsx(
                            'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5',
                            checkingModelHealth
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          )}
                          title="检查模型可用性"
                        >
                          {checkingModelHealth ? (
                            <>
                              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              检查中...
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              健康检查
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsModelCollapsed(!isModelCollapsed)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                          title={isModelCollapsed ? '展开' : '折叠'}
                        >
                          <svg
                            className={clsx('w-5 h-5 transition-transform', isModelCollapsed ? '' : 'rotate-180')}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    {/* Model Health Check Result */}
                    {modelHealthResult && (
                      <div className={clsx(
                        'mb-4 p-3 rounded-lg text-sm',
                        modelHealthResult.valid
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : 'bg-red-50 border border-red-200 text-red-700'
                      )}>
                        <div className="flex items-center gap-2">
                          {modelHealthResult.valid ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                          {modelHealthResult.message}
                        </div>
                      </div>
                    )}
<div className={clsx('space-y-4', isModelCollapsed && 'hidden')}>
                       <div className="grid grid-cols-2 gap-4">
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             模型标识 (model_use)
                           </label>
                           <input
                             type="text"
                             value={currentModel.model_use}
                             onChange={(e) => handleUpdateModel(selectedModel, { model_use: e.target.value })}
                             disabled={disabled}
                             placeholder="例如: gpt-4"
                             className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                           />
                         </div>
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             实际模型名称 (model_name)
                           </label>
                           <input
                             type="text"
                             value={currentModel.model_name}
                             onChange={(e) => handleUpdateModel(selectedModel, { model_name: e.target.value })}
                             disabled={disabled}
                             placeholder="例如: gpt-4-0125-preview"
                             className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                           />
                         </div>
                       </div>

                       <div className="grid grid-cols-2 gap-4">
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             模型类型 (model_type)
                           </label>
                           <select
                             value={currentModel.model_type || 'chat'}
                             onChange={(e) => handleUpdateModel(selectedModel, { model_type: e.target.value })}
                             disabled={disabled}
                             className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                           >
                             <option value="chat">对话</option>
                             <option value="write">写作</option>
                             <option value="label">标签</option>
                             <option value="summary">摘要</option>
                             <option value="image">图像生成</option>
                             <option value="graphiti">Graphiti</option>
                             <option value="embedding">Embedding</option>
                             <option value="rerank">Rerank</option>
                           </select>
                           <p className="mt-1 text-xs text-gray-500">用于动态路由选择模型</p>
                         </div>
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             优先级
                           </label>
                           <input
                             type="number"
                             min={0}
                             value={currentModel.priority ?? 0}
                             onChange={(e) => {
                               const val = parseInt(e.target.value, 10);
                               if (!isNaN(val) && val >= 0) {
                                 handleUpdateModel(selectedModel, { priority: val });
                               }
                             }}
                             disabled={disabled}
                             className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                           />
                           <p className="mt-1 text-xs text-gray-500">0 最高，数字越大优先级越低</p>
                         </div>
                       </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          模型级 Base URL（可选）
                        </label>
                        <input
                          type="url"
                          value={currentModel.base_url || ''}
                          onChange={(e) => handleUpdateModel(selectedModel, { base_url: e.target.value })}
                          disabled={disabled}
                          placeholder="覆盖提供商/池级 Base URL"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                        />
                      </div>

                      {/* Model Health */}
                      {currentModel.health && (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">健康状态</h4>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">可用性</span>
                              <StatusBadge
                                variant={currentModel.health.available ? 'success' : 'error'}
                                size="sm"
                                dot
                              >
                                {currentModel.health.available ? '可用' : '不可用'}
                              </StatusBadge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">延迟</span>
                              <span className="font-medium text-gray-800">
                                {currentModel.health.latency_ms > 0 ? `${currentModel.health.latency_ms}ms` : '-'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">错误次数</span>
                              <span className="font-medium text-gray-800">{currentModel.health.error_count}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Model Quota */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          模型级别配额（可选）
                        </label>
                        <QuotaEditor
                          value={currentModel.quota || {}}
                          onChange={(quota) => handleUpdateModel(selectedModel, { quota })}
                          disabled={disabled}
                          compact
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Priorities View */}
      {activeView === 'priorities' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <PriorityDragger
            items={priorities}
            onChange={handlePriorityChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* Quota Usage */}
      {Object.keys(quotaUsage).length > 0 && activeView === 'providers' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">当前配额使用情况</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(quotaUsage).map(([provider, pools]) =>
              Object.entries(pools).map(([poolIndex, quotas]) => (
                <div key={`${provider}-${poolIndex}`} className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {provider} / 池 #{poolIndex}
                  </div>
                  <div className="space-y-2">
                    {quotas.token_per_day && (
                      <QuotaProgress
                        quota={{ ...quotas.token_per_day, total: quotas.token_per_day.total || false }}
                        label="Token/天"
                        size="sm"
                      />
                    )}
                    {quotas.token_per_hour && (
                      <QuotaProgress
                        quota={{ ...quotas.token_per_hour, total: quotas.token_per_hour.total || false }}
                        label="Token/小时"
                        size="sm"
                      />
                    )}
                    {quotas.request_per_minute && (
                      <QuotaProgress
                        quota={{ ...quotas.request_per_minute, total: quotas.request_per_minute.total || false }}
                        label="请求/分钟"
                        size="sm"
                      />
                    )}
                    {quotas.concurrency && (
                      <QuotaProgress
                        quota={{ ...quotas.concurrency, total: quotas.concurrency.total || false }}
                        label="并发数"
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="p-4 bg-gray-50 rounded-lg text-xs text-gray-600">
        <strong>说明：</strong>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>提供商：LLM API 服务商配置，包含 Base URL 和池列表</li>
          <li>池：独立的 API Key 配置单元，可以配置独立的配额限制</li>
          <li>模型：具体的模型配置，可以设置模型级别的配额</li>
          <li>优先级：请求路由时的池选择顺序，顶部优先级最高</li>
        </ul>
      </div>

      <CreateEntityDialog
        isOpen={createDialog === 'provider'}
        title="添加新提供商"
        fields={providerCreateFields}
        onConfirm={handleCreateProvider}
        onCancel={handleCancelCreate}
        loading={creating}
        error={createError}
      />

      <CreateEntityDialog
        isOpen={createDialog === 'pool'}
        title="添加新池"
        fields={poolCreateFields}
        onConfirm={handleCreatePool}
        onCancel={handleCancelCreate}
        loading={creating}
        error={createError}
      />

      <CreateEntityDialog
        isOpen={createDialog === 'model'}
        title="添加新模型"
        fields={modelCreateFields}
        onConfirm={handleCreateModel}
        onCancel={handleCancelCreate}
        loading={creating}
        error={createError}
      />
    </div>
  );
};

export default SettingsEditor;
